import wooCommerceIcon from 'assets/images/integrations/woocommerce.svg';

import type { ConversionRule } from 'modules/pixel/pixelApiTypes';

import { CodeBlock } from '../CodeBlock';
import type { IntegrationContext, IntegrationSnippet, PixelIntegration } from './types';

/**
 * WooCommerce — install the universal pixel via WP header (covered in the WordPress tab) and
 * fire purchase conversions from the {@code woocommerce_thankyou} hook so we capture the live
 * order total, currency, and order id.
 */
export const wooCommerceIntegration: PixelIntegration = {
  id: 'woocommerce',
  label: 'WooCommerce',
  description:
    'Hook into woocommerce_thankyou so the purchase conversion fires with the live order total, currency, and order ID.',
  icon: <img src={wooCommerceIcon} alt="" className="h-4 w-4" />,
  docsHref: 'https://woocommerce.com/document/custom-tracking-code-for-the-thanks-page/',
  renderSetup: (ctx) => (
    <div className="flex flex-col gap-4">
      <Step number={1} title="Install the universal pixel site-wide (WordPress tab)">
        <p className="text-base-xs text-primary-gray-500">
          Follow the <strong>WordPress</strong> integration to put the universal pixel in{' '}
          <code className="rounded bg-primary-gray-100 px-1 font-mono text-[11px]">
            &lt;head&gt;
          </code>{' '}
          of every page (via WPCode or similar). Below is the same{' '}
          <code className="rounded bg-primary-gray-100 px-1 font-mono text-[11px]">
            &lt;script&gt;
          </code>{' '}
          for reference:
        </p>
        <CodeBlock language="html" code={ctx.pixelScriptTag} />
      </Step>
      <Step number={2} title="Add a Code Snippet for the purchase hook">
        <p className="text-base-xs text-primary-gray-500">
          Use a snippet plugin (e.g. <strong>Code Snippets</strong>, or the <em>PHP Snippets</em>{' '}
          add-on in WPCode) to register the action. Avoid editing <code>functions.php</code>{' '}
          directly so theme updates don&apos;t wipe it.
        </p>
        <p className="mt-2 text-base-xs text-primary-gray-500">
          Paste the PHP snippet from Step 3 into a new snippet, set it to{' '}
          <strong>Run everywhere</strong>, save and activate.
        </p>
      </Step>
      <Step number={3} title="Exclude the thank-you page from caching">
        <p className="text-base-xs text-primary-gray-500">
          Cached confirmation pages are the #1 cause of missing or duplicated purchase events. In
          your caching plugin, exclude{' '}
          <code className="rounded bg-primary-gray-100 px-1 font-mono text-[11px]">
            /checkout/order-received/*
          </code>{' '}
          from full-page caching.
        </p>
      </Step>
    </div>
  ),
  buildSnippetForRule: (rule, ctx) => buildWooCommerceSnippet(rule, ctx),
};

function Step({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-electric-100 text-[11px] font-semibold text-primary-electric-700">
          {number}
        </span>
        <h4 className="text-base-sm font-medium text-primary-gray-900">{title}</h4>
      </div>
      <div className="pl-7">{children}</div>
    </div>
  );
}

/**
 * For checkout rules with extractors, generate the PHP+JS thankyou-hook snippet so order
 * values get pulled dynamically. For other categories, fall back to a simple JS-only snippet
 * since the woocommerce_thankyou hook only meaningfully runs on purchase confirmation.
 */
function buildWooCommerceSnippet(
  rule: ConversionRule,
  ctx: IntegrationContext,
): IntegrationSnippet {
  if (rule.category !== 'checkout') {
    // Non-checkout categories don't need WC-specific hooks — use the generic WP snippet shape.
    const lines: string[] = [`  'category': ${JSON.stringify(rule.category)}`];

    if (rule.subcategory) lines.push(`  'subcategory': ${JSON.stringify(rule.subcategory)}`);
    const code = [
      '<script>',
      'if (window.CMPIXEL && CMPIXEL.fire) {',
      `  CMPIXEL.fire('${ctx.pixelCode}', {`,
      lines.join(',\n'),
      '  });',
      '}',
      '</script>',
    ].join('\n');

    return {
      code,
      language: 'html',
      label: 'Paste into the relevant page or HTML block',
      note: 'Non-checkout rules don\'t need the WooCommerce hook — use this anywhere on the site.',
    };
  }

  // Checkout: full PHP+JS snippet pulling values from the WC_Order.
  const subcategoryLine = rule.subcategory
    ? `\n            'subcategory': '<?php echo esc_js('${rule.subcategory.replace(/'/g, '\\\'')}'); ?>',`
    : '';

  const code = [
    '// Add to your child theme\'s functions.php or to a snippet via WPCode / Code Snippets.',
    'add_action( \'woocommerce_thankyou\', \'coolai_pixel_purchase\', 20, 1 );',
    'function coolai_pixel_purchase( $order_id ) {',
    '    if ( ! $order_id ) return;',
    '    if ( get_post_meta( $order_id, \'_coolai_pixel_fired\', true ) ) return;',
    '    $order = wc_get_order( $order_id );',
    '    if ( ! $order ) return;',
    '    update_post_meta( $order_id, \'_coolai_pixel_fired\', 1 );',
    '    $amount   = $order->get_total();',
    '    $currency = $order->get_currency();',
    '    $order_no = $order->get_order_number();',
    '    ?>',
    '    <script>',
    '    if (window.CMPIXEL && CMPIXEL.fire) {',
    `        CMPIXEL.fire('${ctx.pixelCode}', {`,
    `            'category': 'checkout',${subcategoryLine}`,
    '            \'amount\': \'<?php echo esc_js( $amount ); ?>\',',
    '            \'currency\': \'<?php echo esc_js( $currency ); ?>\',',
    '            \'order_number\': \'<?php echo esc_js( $order_no ); ?>\'',
    '        });',
    '    }',
    '    </script>',
    '    <?php',
    '}',
  ].join('\n');

  return {
    code,
    language: 'js', // PHP highlighting isn't worth pulling in another lang — JS keyword color is close enough.
    label: 'PHP snippet for functions.php / WPCode',
    note: 'The _coolai_pixel_fired meta guards against duplicate fires when shoppers refresh the thank-you page.',
  };
}
